import { z } from 'zod';
/**
 * How many stale visits ONE invocation of the purge processes. The seam is bounded on
 * purpose (S16 Constraint 5): a scheduler drives it to completion by calling it repeatedly
 * until `visitsRemaining` is 0, which is the shape an EventBridge rule or a queue consumer
 * will want once `infra/` exists, and which keeps a single request's transaction short
 * enough that no gateway timeout can leave a batch half-applied.
 *
 * 200 rather than 1000: one invocation does up to 200 sequential object deletes through the
 * storage port before it opens its transaction, and 200 keeps that pre-transaction stretch
 * inside a normal request budget. Raising it trades scheduler round-trips for request
 * latency; it is not a correctness knob.
 */
export const RETENTION_PURGE_MAX_VISITS = 200;
/**
 * What `POST /api/web/visitor/retention-purge` answers (PRD-008 RET-02, RET-05).
 *
 * AN ALLOW-LIST OF COUNTS AND A WINDOW (docs/008 S7). There is deliberately no list of
 * purged visit ids, person ids, names or addresses: a response that enumerated exactly who
 * was erased would rebuild, in the access log and the caller's browser, the identifying
 * index the purge just destroyed.
 *
 * `retentionDays === null` (and therefore `cutoff === null`) is the NO-OP answer — the
 * tenant has no retention policy, so nothing was in range and every count is 0. It is a 200,
 * not a 4xx: "you have not asked me to delete anything" is a valid state, not a caller error.
 *
 * `visitsPurged` is the number of visits that carried erasable PII when this invocation
 * started and carry none now — never more than `batchLimit`. `visitsRemaining` is measured
 * AFTER the batch, so a scheduler polls it to decide whether to call again; it reaching 0 is
 * the definition of "this tenant is fully purged for this window".
 */
export const visitorRetentionPurgeResultDTOSchema = z.object({
    retentionDays: z.number().int().nullable(),
    cutoff: z.string().datetime().nullable(),
    batchLimit: z.number().int().positive(),
    visitsPurged: z.number().int().nonnegative(),
    visitsRemaining: z.number().int().nonnegative(),
    peopleAnonymized: z.number().int().nonnegative(),
    photosDeleted: z.number().int().nonnegative(),
    acceptancesRedacted: z.number().int().nonnegative(),
    invitesRedacted: z.number().int().nonnegative(),
    /** Signature-image `StoredFile` objects deleted (document-signing PDF signing). */
    signaturesDeleted: z.number().int().nonnegative(),
    /** Generated signed-PDF `StoredFile` objects deleted. `0` on a batch where no acceptance had
     *  yet had its PDF lazily generated — nothing to delete is not an error. */
    signedDocumentsDeleted: z.number().int().nonnegative(),
});
//# sourceMappingURL=visitor-retention-purge.js.map